// Ask the assistant about the files on quality records.
//
// THE SCOPE IS THE PAGE'S, NOT THE MODEL'S. This panel is handed a scope by whatever opened it --
// one file, one record's files, or the records somebody ticked in a list -- and it sends that scope
// with every turn. There is no tool the model can call to widen it. That is deliberate and it is the
// difference between this and the NavBar assistant beside it: a model that could choose which
// records to read would be one prompt away from choosing records the reader cannot see, and the
// prompt could arrive inside an attachment, which is the content being read.
//
// WHAT WAS LEFT OUT IS SHOWN, ALWAYS, above the answer rather than below it. An answer about eight of
// twelve selected documents reads exactly like an answer about twelve, and the reader is the only
// person who can tell that it matters.
//
// THE CITATIONS ARE THE POINT. An answer about a controlled document that cannot say which page it
// came from is an opinion. Each one is rendered as the record, the page and the quote, so somebody
// can open the file and check it.
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, CircularProgress, Divider, IconButton, Popover, TextField, Tooltip, Typography} from "@mui/material";
import {AutoAwesomeOutlined, SendRounded} from "@mui/icons-material";
import {useSessionContext} from "../../util/SessionContext";
import {usePersona} from "../Common/PersonaHeaderInfo";
import {
    askAboutAttachments, AttachmentAnswer, AttachmentScope, AttachmentTurn,
} from "../../data/attachmentQuestions";

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const TEXT = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E4E6E8";
const BLUE = "#1498D8";
const AMBER = "#8A6100";

type Entry =
    | {role: "user"; text: string}
    | {role: "assistant"; text: string; answer: AttachmentAnswer}
    | {role: "error"; text: string};

type Props = {
    scope: AttachmentScope;
    /** What the panel says it is asking about: "SOP-00012", "4 selected records", a filename. */
    scopeLabel: string;
    /** The launcher. Defaults to the assistant's own sparkle icon at the size a row wants. */
    children?: (open: (e: React.MouseEvent<HTMLElement>) => void) => React.ReactNode;
    tooltip?: string;
    /** Rendered under the question box; the place to say "these are drafts" or similar. */
    note?: string | null;
};

const SUGGESTIONS = [
    "Summarise what these documents require.",
    "What records must be kept, and for how long?",
    "Who approves this, and what must they check?",
    "Is anything in these documents inconsistent with the others?",
];

export default function AskAboutAttachments({scope, scopeLabel, children, tooltip, note}: Props) {
    const sessionContext = useSessionContext();
    // THE VIEW IS NAMED BECAUSE IT CHANGES THE ANSWER. Scope is the ACTIVE view, not the widest the
    // person holds -- settled with Ed on 2026-10-21: "Users can be trained to understand the
    // implications of visibility. Security must track with this established norm." Which makes the
    // same question return less in Position view than in Organization view, correctly, and
    // surprisingly. A reader who can see which view answered can tell the difference between "we
    // have no such procedure" and "none that this view reaches".
    const persona = usePersona();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [entries, setEntries] = useState<Entry[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    const listRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [entries, loading]);

    // A NEW SCOPE IS A NEW CONVERSATION. Re-ticking the list under an open panel would otherwise
    // leave answers about the old selection sitting above questions about the new one, and nothing
    // on screen would say which was which.
    const scopeKey = useMemo(() => JSON.stringify(scope), [scope]);
    useEffect(() => { setEntries([]); }, [scopeKey]);

    // Prose only, and the server rebuilds the documents from the scope on every turn.
    const priorTurns = (): AttachmentTurn[] =>
        entries.filter((one): one is Exclude<Entry, {role: "error"}> => one.role !== "error")
               .map(one => ({role: one.role, text: one.text}));

    const ask = useCallback(async (question: string) => {
        const text = question.trim();
        if (!text || loading) return;
        const turns = priorTurns();
        setInput("");
        setEntries(prev => [...prev, {role: "user", text}]);
        setLoading(true);
        try {
            const answer = await askAboutAttachments(sessionContext, text, scope, turns);
            setEntries(prev => [...prev, {
                role: "assistant", answer,
                text: answer.answer || "(the model returned nothing)",
            }]);
        } catch (problem: any) {
            // THE SERVER'S OWN SENTENCE WHEN IT WROTE ONE. Every refusal here is user_facing and
            // says something useful -- which records are out of reach, how many files is too many,
            // that no model is enabled. Replacing those with "something went wrong" would throw away
            // the only part of the failure the reader can act on.
            const said = problem?.response?.data?.Message || problem?.response?.data?.message;
            setEntries(prev => [...prev, {
                role: "error",
                text: said || "Something went wrong reading those files. Please try again.",
            }]);
        } finally {
            setLoading(false);
        }
    }, [loading, scope, sessionContext, entries]);          // eslint-disable-line react-hooks/exhaustive-deps

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            ask(input);
        }
    };

    const open = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);

    return (
        <>
            {children ? children(open) : (
                <Tooltip title={tooltip || `Ask about ${scopeLabel}`}>
                    <IconButton size="small" onClick={open} aria-label="ask about these files">
                        <AutoAwesomeOutlined sx={{fontSize: 18, color: BLUE}}/>
                    </IconButton>
                </Tooltip>
            )}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{paper: {sx: {width: 460, maxWidth: "94vw", height: 580, maxHeight: "82vh",
                                         display: "flex", flexDirection: "column", fontFamily: FONT}}}}
            >
                <Box sx={{px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <AutoAwesomeOutlined sx={{color: BLUE, fontSize: 20}}/>
                        <Typography sx={{fontSize: 15, fontWeight: 700, color: TEXT, fontFamily: FONT}}>
                            Ask about the attachments
                        </Typography>
                    </Box>
                    {/* WHAT IS IN SCOPE, IN WORDS, at the top of the panel. The same question
                        produces a different answer against one record and against twelve, and the
                        reader must not have to remember which they opened. */}
                    <Typography sx={{fontSize: 12, color: MUTED, fontFamily: FONT, mt: 0.5}}>
                        {scopeLabel}
                    </Typography>
                    {persona ? (
                        <Typography sx={{fontSize: 11.5, color: MUTED, fontFamily: FONT, mt: 0.25}}>
                            Read as <b>{persona.type}</b>
                            {persona.name ? ` · ${persona.nameLabel}: ${persona.name}` : ""}
                            {" "}— only what this view reaches is searched.
                        </Typography>
                    ) : null}
                </Box>

                <Box ref={listRef} sx={{flex: 1, overflowY: "auto", px: 2, py: 1.5,
                                        display: "flex", flexDirection: "column", gap: 1.25}}>
                    {entries.length === 0 && !loading ? (
                        <Box>
                            <Typography sx={{fontSize: 13, color: TEXT, fontFamily: FONT, mb: 1}}>
                                Questions are answered only from the files themselves, with the page
                                they came from.
                            </Typography>
                            {SUGGESTIONS.map(one => (
                                <Typography key={one} onClick={() => ask(one)}
                                            sx={{fontSize: 12.5, color: BLUE, fontFamily: FONT,
                                                 cursor: "pointer", py: 0.4}}>
                                    {one}
                                </Typography>
                            ))}
                        </Box>
                    ) : null}

                    {entries.map((entry, index) => (
                        <Box key={index}
                             sx={{alignSelf: entry.role === "user" ? "flex-end" : "flex-start",
                                  maxWidth: entry.role === "user" ? "88%" : "100%",
                                  px: 1.5, py: 1, borderRadius: 2, fontFamily: FONT, fontSize: 13.5,
                                  lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
                                  color: entry.role === "error" ? "#B3261E" : TEXT,
                                  backgroundColor: entry.role === "user" ? "#E9F4FB"
                                      : entry.role === "error" ? "#FCECEA" : "#F5F6F7",
                                  border: entry.role === "user" ? `1px solid ${BORDER}` : "none"}}>
                            {entry.role === "assistant" ? (
                                <Caveats answer={entry.answer}/>
                            ) : null}
                            {entry.text}
                            {entry.role === "assistant" ? (
                                <Sources answer={entry.answer}/>
                            ) : null}
                        </Box>
                    ))}

                    {loading ? (
                        <Box sx={{alignSelf: "flex-start", px: 1.5, py: 1, display: "flex",
                                  alignItems: "center", gap: 1}}>
                            <CircularProgress size={13} sx={{color: BLUE}}/>
                            <Typography sx={{fontSize: 12.5, color: MUTED, fontFamily: FONT}}>
                                Reading the files…
                            </Typography>
                        </Box>
                    ) : null}
                </Box>

                <Box sx={{borderTop: `1px solid ${BORDER}`, px: 1.5, py: 1.25}}>
                    <Box sx={{display: "flex", alignItems: "flex-end", gap: 1}}>
                        <TextField value={input} onChange={(e) => setInput(e.target.value)}
                                   onKeyDown={onKeyDown}
                                   placeholder="Ask about these files…"
                                   multiline maxRows={4} fullWidth size="small"
                                   sx={{"& .MuiInputBase-input": {fontFamily: FONT, fontSize: 13.5}}}/>
                        <IconButton onClick={() => ask(input)} disabled={!input.trim() || loading}
                                    aria-label="send" sx={{color: BLUE}}>
                            <SendRounded/>
                        </IconButton>
                    </Box>
                    <Typography sx={{fontSize: 10.5, color: MUTED, fontFamily: FONT, mt: 0.5,
                                     textAlign: "center"}}>
                        {note || "Answered from these files only. Nothing is created or changed."}
                    </Typography>
                </Box>
            </Popover>
        </>
    );
}

/** What was NOT read, above the answer. Never below it, and never omitted. */
function Caveats({answer}: {answer: AttachmentAnswer}) {
    const lines: string[] = [];
    if (answer.recordsNotVisible > 0) {
        lines.push(`${answer.recordsNotVisible} of the ${answer.recordsAsked} records you selected `
                   + `are not visible to your current persona and were left out.`);
    }
    for (const one of answer.unreadable) {
        // THE RECORD AND THE FILENAME, BOTH. A record can carry five files, and a caveat naming only
        // the record does not tell the reader which one to convert or replace.
        const named = one.label && one.label !== one.name ? `${one.label} — ${one.name}` : one.name;
        lines.push(`${named} was not read — ${one.why}.`);
    }
    if (lines.length === 0) return null;
    return (
        <Box sx={{mb: 0.75, pb: 0.75, borderBottom: `1px solid ${BORDER}`}}>
            {lines.map(line => (
                <Typography key={line} sx={{fontSize: 11.5, color: AMBER, fontFamily: FONT}}>
                    {line}
                </Typography>
            ))}
        </Box>
    );
}

/** The citations, and what was read. The part somebody checks the answer against. */
function Sources({answer}: {answer: AttachmentAnswer}) {
    const [open, setOpen] = useState(true);
    if (answer.citations.length === 0 && answer.read.length === 0) return null;
    return (
        <Box sx={{mt: 1}}>
            <Divider sx={{my: 0.75}}/>
            <Typography onClick={() => setOpen(!open)}
                        sx={{fontSize: 11.5, color: MUTED, fontFamily: FONT, cursor: "pointer"}}>
                {answer.citations.length > 0
                    ? `${answer.citations.length} citation${answer.citations.length === 1 ? "" : "s"}`
                    : "Files read"}
                {" "}· {answer.read.length} file{answer.read.length === 1 ? "" : "s"} read
                {answer.model ? ` · ${answer.model}` : ""} {open ? "▾" : "▸"}
            </Typography>
            {open ? (
                <Box sx={{mt: 0.5}}>
                    {answer.citations.map((one, index) => (
                        <Box key={index} sx={{mb: 0.6}}>
                            <Typography sx={{fontSize: 11.5, color: TEXT, fontFamily: FONT,
                                             fontWeight: 600}}>
                                {one.document}
                                {one.page ? ` — page ${one.page}${
                                    one.throughPage && one.throughPage !== one.page
                                        ? `–${one.throughPage}` : ""}` : ""}
                            </Typography>
                            {one.quote ? (
                                <Typography sx={{fontSize: 11.5, color: MUTED, fontFamily: FONT,
                                                 fontStyle: "italic"}}>
                                    “{one.quote}”
                                </Typography>
                            ) : null}
                        </Box>
                    ))}
                    {/* THE FILES READ, listed even when nothing was cited. An image cannot be quoted,
                        so an answer resting on a drawing has no citations at all -- and without this
                        line it would look as though nothing had been read. */}
                    {answer.citations.length === 0 ? (
                        <Typography sx={{fontSize: 11.5, color: MUTED, fontFamily: FONT}}>
                            {answer.read.map(one => one.label || one.name).join(", ")}
                        </Typography>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}
